import {
  IsString,
  IsNumber,
  IsIn,
  IsBoolean,
  IsOptional,
  IsDefined,
  ValidateNested,
  Min,
  Max,
} from 'class-validator';
import { Type, Transform } from 'class-transformer';
import { IsRgbaOrReference } from '@builder4/shared/dto/styles/validators/IsRgbaOrReference';
import {
  VisibilityStateDto,
  PositionAnchorsStateDto,
  ShadowSettingsDto,
  SHADOW_TYPE_VALUES,
  type ShadowType,
} from '@builder4/shared/dto/craft/props/shared';
import { IsTRBLNumberCoords } from '@builder4/shared/dto/craft/validators/IsTRBLNumberCoords';
import { IsFollowupAction } from '@builder4/shared/dto/craft/validators/IsFollowupAction';
import { RgbaDto } from '@builder4/shared/dto/styles';
import {
  ANIMATION_TYPES,
  ANIMATION_EASINGS,
  HOVER_EFFECT_TYPES,
} from '@builder4/shared/types/animation';

const POSITION_VALUES = ['relative', 'absolute', 'fixed', 'sticky'] as const;
const FILL_SPACE_VALUES = ['yes', 'no'] as const;
const MAX_WIDTH_UNIT_VALUES = ['px', '%'] as const;
const FIELD_SHADOW_TYPE_VALUES = ['none', 'drop'] as const;

export type ActionType =
  | { type: 'remain'; url?: never; pageId?: never }
  | { type: 'default'; url?: never; pageId?: never }
  | { type: 'redirect'; url: string; pageId?: never }
  | { type: 'landing'; pageId?: string; url: string };

/**
 * Breakpoint-aware properties for Form element.
 * Derived from getDetachablePropsForNode('Form'):
 * - DETACHABLE_PROPS_REGISTRY: width, height, fillSpace, flexBasis
 * - POSITION_PROPS: position, positionAnchorsState, left, right, top, bottom, zIndex
 * - BOUNDING_BOX_PROPS: padding, margin, borderRadius
 * - MAX_WIDTH_PROPS: maxWidth, maxWidthUnit
 */
export class FormBreakpointPropsDto {
  // From DETACHABLE_PROPS_REGISTRY[FORM_NODE_NAME]
  @IsOptional()
  @IsString()
  width?: string;

  @IsOptional()
  @IsString()
  height?: string;

  @IsOptional()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace?: (typeof FILL_SPACE_VALUES)[number];

  @IsOptional()
  @IsString()
  flexBasis?: string;

  // From BOUNDING_BOX_PROPS
  @IsOptional()
  @IsTRBLNumberCoords()
  padding?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  margin?: [number, number, number, number];

  @IsOptional()
  @IsTRBLNumberCoords()
  borderRadius?: [number, number, number, number];

  // From POSITION_PROPS
  @IsOptional()
  @IsIn([...POSITION_VALUES])
  position?: (typeof POSITION_VALUES)[number];

  @IsOptional()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState?: PositionAnchorsStateDto;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsOptional()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex?: number;

  // From MAX_WIDTH_PROPS
  @IsOptional()
  @IsNumber()
  @Min(0)
  maxWidth?: number;

  @IsOptional()
  @IsIn([...MAX_WIDTH_UNIT_VALUES])
  maxWidthUnit?: (typeof MAX_WIDTH_UNIT_VALUES)[number];
}

export class FormPropsDto {
  // Business props
  @IsOptional()
  @IsString()
  id?: string;

  @IsDefined()
  @IsString()
  name!: string;

  @Transform(({ obj }) => obj.followupAction)
  @IsDefined()
  @IsFollowupAction()
  followupAction!: ActionType;

  // Field sizing/spacing
  @IsDefined()
  @IsString()
  fieldWidth!: string;

  @IsOptional()
  @IsString()
  fieldMaxWidth?: string;

  @IsOptional()
  @IsString()
  fieldMinWidth?: string;

  @IsDefined()
  @IsTRBLNumberCoords()
  fieldMargin!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  fieldBorderRadius!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  fieldInputPadding!: [number, number, number, number];

  // Label text styles
  @IsOptional()
  @IsString()
  fieldLabelFontFamily?: string;

  @IsOptional()
  @IsString()
  fieldLabelFontSize?: string;

  @IsOptional()
  fieldLabelFontWeight?: string | number;

  @IsOptional()
  @IsRgbaOrReference()
  fieldLabelColor?: RgbaDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  fieldLabelInvalidColor?: RgbaDto;

  @IsOptional()
  @IsNumber()
  fieldLabelSpacing?: number;

  // Input text styles
  @IsOptional()
  @IsString()
  fieldInputFontFamily?: string;

  @IsOptional()
  @IsString()
  fieldInputFontSize?: string;

  @IsOptional()
  fieldInputFontWeight?: string | number;

  @IsOptional()
  @IsRgbaOrReference()
  fieldInputTextColor?: RgbaDto | string;

  @IsOptional()
  @IsRgbaOrReference()
  fieldInputHoverTextColor?: RgbaDto | string;

  @IsOptional()
  @IsNumber()
  fieldInputSpacing?: number;

  // Input vessel colors
  @IsOptional()
  @IsRgbaOrReference()
  fieldInputBackgroundColor?: RgbaDto | string;

  @IsOptional()
  @IsRgbaOrReference()
  fieldInputBorderColor?: RgbaDto | string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  fieldInputBorderWidth?: number;

  @IsOptional()
  @IsRgbaOrReference()
  fieldInputHoverBackgroundColor?: RgbaDto | string;

  @IsOptional()
  @IsRgbaOrReference()
  fieldInputHoverBorderColor?: RgbaDto | string;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  fieldInputInvalidBackgroundColor?: RgbaDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  fieldInputInvalidBorderColor?: RgbaDto;

  // Helper text
  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  fieldHelperTextColor?: RgbaDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => RgbaDto)
  fieldHelperTextInvalidColor?: RgbaDto;

  // Checkbox
  @IsOptional()
  @IsRgbaOrReference()
  fieldCheckboxCheckedColor?: RgbaDto | string;

  // Shadow
  @IsOptional()
  @IsIn([...FIELD_SHADOW_TYPE_VALUES])
  fieldShadowType?: (typeof FIELD_SHADOW_TYPE_VALUES)[number];

  @IsOptional()
  @ValidateNested()
  @Type(() => ShadowSettingsDto)
  fieldShadowSettings?: ShadowSettingsDto;

  // Layout sizing
  @IsDefined()
  @IsIn([...FILL_SPACE_VALUES])
  fillSpace!: (typeof FILL_SPACE_VALUES)[number];

  @IsDefined()
  @IsString()
  flexBasis!: string;

  @IsDefined()
  @IsString()
  width!: string;

  @IsDefined()
  @IsString()
  height!: string;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minWidth!: number;

  @IsDefined()
  @IsNumber()
  @Min(0)
  minHeight!: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  maxWidth?: number;

  @IsOptional()
  @IsIn([...MAX_WIDTH_UNIT_VALUES])
  maxWidthUnit?: (typeof MAX_WIDTH_UNIT_VALUES)[number];

  // Spacing
  @IsDefined()
  @IsTRBLNumberCoords()
  padding!: [number, number, number, number];

  @IsDefined()
  @IsTRBLNumberCoords()
  margin!: [number, number, number, number];

  // Borders & colors
  @IsDefined()
  @IsTRBLNumberCoords()
  borderRadius!: [number, number, number, number];

  @IsDefined()
  @IsNumber()
  @Min(0)
  borderWidth!: number;

  @IsOptional()
  @IsRgbaOrReference()
  borderColor?: RgbaDto | string;

  @IsOptional()
  @IsRgbaOrReference()
  hoverBorderColor?: RgbaDto | string;

  @IsOptional()
  @IsRgbaOrReference()
  backgroundColor?: RgbaDto | string;

  // Position
  @IsDefined()
  @IsIn([...POSITION_VALUES])
  position!: (typeof POSITION_VALUES)[number];

  @IsOptional()
  @IsString()
  top?: string;

  @IsOptional()
  @IsString()
  left?: string;

  @IsOptional()
  @IsString()
  right?: string;

  @IsOptional()
  @IsString()
  bottom?: string;

  @IsDefined()
  @ValidateNested()
  @Type(() => PositionAnchorsStateDto)
  positionAnchorsState!: PositionAnchorsStateDto;

  @IsDefined()
  @IsNumber()
  @Min(-1)
  @Max(100)
  zIndex!: number;

  // Visibility
  @IsDefined()
  @ValidateNested()
  @Type(() => VisibilityStateDto)
  visibility!: VisibilityStateDto;

  // Aspect ratio
  @IsDefined()
  @IsBoolean()
  aspectRatioLock!: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  aspectRatio?: number;

  // Animation
  @IsOptional()
  @IsIn([...ANIMATION_TYPES])
  animationType?: (typeof ANIMATION_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(100)
  @Max(2000)
  animationDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  animationDelay?: number;

  @IsOptional()
  @IsIn([...ANIMATION_EASINGS])
  animationEasing?: (typeof ANIMATION_EASINGS)[number];

  // Hover effect
  @IsOptional()
  @IsIn([...HOVER_EFFECT_TYPES])
  hoverEffect?: (typeof HOVER_EFFECT_TYPES)[number];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(2000)
  hoverTransitionDuration?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Max(200)
  hoverIntensity?: number;
  // Container shadow
  @IsOptional()
  @IsIn([...SHADOW_TYPE_VALUES])
  shadowType?: ShadowType;

  @IsOptional()
  @ValidateNested()
  @Type(() => ShadowSettingsDto)
  shadowSettings?: ShadowSettingsDto;

  // Breakpoint overrides
  @IsOptional()
  @ValidateNested()
  @Type(() => FormBreakpointPropsDto)
  tablet?: FormBreakpointPropsDto;

  @IsOptional()
  @ValidateNested()
  @Type(() => FormBreakpointPropsDto)
  mobile?: FormBreakpointPropsDto;
}
